import React, { useEffect, useState } from 'react';
import { Button, Checkbox, Input, message, Radio, Card, Typography, InputNumber } from 'antd';
import { TLessonAnswerUser, TLessonQuestion } from '../../models/courses/lesson';
import { useReferential } from '../../context/ReferentialContext';
import { CODIFICATION_QUESTION_TYPE } from '../../constant/codification';
import { useUserApi } from '../../api/user/userApp';
import { useTranslation } from 'react-i18next';
import { useTilmiAuth } from '../../hooks/useTilmiAuth';
import { CODIFICATION_TYPE_USERS } from '../../constant/codification';
import './forms.css';

const { Title, Text } = Typography;

interface QuestionViewerProps {
  question: TLessonQuestion;
  onNextLesson: () => void;
  // When true, force teacher preview regardless of auth and skip persistence
  previewTeacher?: boolean;
}

const QuestionViewer: React.FC<QuestionViewerProps> = ({ question, onNextLesson, previewTeacher }) => {
  const { getCodificationById } = useReferential();
  const { getQuestionAnswersByQuestionId, saveQuestionAnswers } = useUserApi();
  const { t } = useTranslation();
  const { getCodeTypeUser } = useTilmiAuth();
  const isTeacher = !!previewTeacher || (getCodeTypeUser() === CODIFICATION_TYPE_USERS.TEACHER);

  const [onSave, setOnSave] = useState<boolean>(false);
  
  // For teachers: pre-fill with correct answers, for students: start empty
  const getInitialAnswers = (): TLessonAnswerUser[] => {
    if (isTeacher && question.answers) {
      const correctAnswers = question.answers.filter(a => a.is_correct);
      return correctAnswers.map(answer => ({
        id: 0,
        answer_id: answer.id,
        userapp_id: 0,
        question_id: question.id,
        user_input: answer.content || '', // Pre-filled with correct answer
      }));
    }
    return []; // Empty for students
  };
  
  const [answers, setAnswers] = useState<TLessonAnswerUser[]>(() => getInitialAnswers());

  const questionType = getCodificationById(question.question_type_id);

  useEffect(() => {
    if (isTeacher) {
      // For teachers: always use pre-filled correct answers, don't load from API
      const initialAnswers = getInitialAnswers();
      setAnswers(initialAnswers);
      return;
    }
    
    // For students: Récupérer les réponses de la question à partir de l'API
    getQuestionAnswersByQuestionId(question.id)
      .then((resp) => {
        setAnswers(resp.data ?? []);
      })
      .catch((err) => {
        message.error(err.response?.data ?? err.message);
      });
    // eslint-disable-next-line
  }, [question, isTeacher]);

  const handleAnswersRadioButton = (idAnswer: number) => {
    const answerSelected = question.answers.find((e) => e.id === idAnswer);
    if (answerSelected) {
      const newAnswer: TLessonAnswerUser = {
        id: 0,
        answer_id: answerSelected.id,
        userapp_id: 0,
        question_id: question.id,
        user_input: '',
      };
      setAnswers([newAnswer]);
    }
  };

  const handleAnswersCheckBox = (idAnswer: number, remove: boolean = false) => {
    if (remove) {
      // Supprimer une réponse de la liste
      setAnswers((prevAnswers) => prevAnswers.filter((e) => e.answer_id !== idAnswer));
    } else {
      // Trouver la réponse à ajouter
      const answerToAdd = question.answers.find((e) => e.id === idAnswer);
      if (answerToAdd) {
        setAnswers((prevAnswers) => {
          // Ajouter la réponse seulement si elle n'est pas déjà présente
          const isAlreadyAdded = prevAnswers.some((e) => e.answer_id === idAnswer);
          if (!isAlreadyAdded) {
            const newAnswer: TLessonAnswerUser = {
              id: 0,
              answer_id: answerToAdd.id,
              userapp_id: 0,
              question_id: question.id,
              user_input: '',
            };

            return [...prevAnswers, newAnswer];
          }
          return prevAnswers;
        });
      }
    }
  };

  const handleAnswersText = (value: string) => {
    if (question.answers.length > 0) {
      const answerSelected = question.answers[0];
      const newAnswer: TLessonAnswerUser = {
        id: 0,
        answer_id: answerSelected.id,
        userapp_id: 0,
        question_id: question.id,
        user_input: value,
      };
      setAnswers([newAnswer]);
    }
  };

  const validateAnswer = () => {
    // In teacher preview, just advance without saving
    if (previewTeacher) {
      onNextLesson();
      return;
    }
    if (!questionType) {
      return;
    }

    // Vérification des réponses pour SINGLE_CHOICE et MULTI_CHOICE
    if (
      questionType.code === CODIFICATION_QUESTION_TYPE.MULTI_CHOICE ||
      questionType.code === CODIFICATION_QUESTION_TYPE.SINGLE_CHOICE
    ) {
      const incorrectSelected = answers.some((answer) =>
        question.answers.some(
          (answerQuestion) => answerQuestion.id === answer.answer_id && !answerQuestion.is_correct
        )
      );

      if (incorrectSelected) {
        // Mauvaise réponse → message uniquement
        message.error(t('exercise.messages.wrongAnswer'));
        return;
      }
    }

    switch (questionType.code) {
      case CODIFICATION_QUESTION_TYPE.SINGLE_CHOICE:
        if (answers.length === 0) {
          message.warning(t('exercise.messages.selectAnswer'));
          return;
        }
        break;

      case CODIFICATION_QUESTION_TYPE.MULTI_CHOICE:
        // Vérifier le minimum de réponses correctes
        if (answers.length < question.min_valid_answers) {
          message.warning(
            t('exercise.messages.minAnswers', {
              count: question.min_valid_answers,
            })
          );
          return;
        }
        break;

      case CODIFICATION_QUESTION_TYPE.TEXT_INPUT:
      case CODIFICATION_QUESTION_TYPE.NUMERIC_INPUT:
        if (question.answers.length > 0 && answers.length > 0) {
          const questionAnswer = question.answers[0];
          const answerUser = answers[0];
          if (
            questionAnswer.content !== '*' &&
            questionAnswer.content.toLowerCase() !== answerUser.user_input.toLowerCase()
          ) {
            message.error(t('exercise.messages.retry'));
            return;
          }
        } else {
          return;
        }
        break;

      default:
        break;
    }

    // Si on arrive ici, la réponse est correcte → on garde le même comportement qu'avant
    setOnSave(true);
    saveQuestionAnswers(answers)
      .then((e) => {
        setAnswers(e.data);
        onNextLesson();
      })
      .catch((err) => {
        message.error(err.response?.data ?? err.message);
      })
      .finally(() => {
        setOnSave(false);
      });
  };

  return (
    <div style={{ padding: '20px', display: 'flex', justifyContent: 'center' }}>
      <Card
        title={<Title level={4}>{t('exercise.questionTitle')}</Title>}
        style={{
          width: '100%',
          maxWidth: '800px',
          margin: '20px auto',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          padding: 20,
        }}
      >
        <div style={{ marginBottom: '20px' }}>
          <Text
            style={{
              fontSize: '16px',
              fontWeight: 500,
              display: 'block',
              lineHeight: '1.5',
              wordWrap: 'break-word',
              whiteSpace: 'normal',
            }}
          >
            {question.content}
          </Text>
        </div>

        {questionType?.code === CODIFICATION_QUESTION_TYPE.SINGLE_CHOICE && (
          <Radio.Group
            onChange={(e) => handleAnswersRadioButton(Number(e.target.value))}
            style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
            value={
              answers.find((e) => question.answers.some((a) => a.id === e.answer_id))
                ?.answer_id || null
            }
          >
            {question.answers.map((answer) => (
              <Radio key={answer.id} value={answer.id} style={{ padding: '5px 10px' }}>
                {answer.content}
              </Radio>
            ))}
          </Radio.Group>
        )}

        {questionType?.code === CODIFICATION_QUESTION_TYPE.MULTI_CHOICE && (
          <Checkbox.Group
            style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
            onChange={(checkedValues) => {
              const selectedIds = new Set(checkedValues.map((value: number) => value));
              question.answers.forEach((answer) => {
                if (selectedIds.has(answer.id)) {
                  handleAnswersCheckBox(answer.id, false);
                } else {
                  handleAnswersCheckBox(answer.id, true);
                }
              });
            }}
            value={answers
              .filter((e) => question.answers.some((a) => a.id === e.answer_id))
              .map((e) => e.answer_id)}
          >
            {question.answers.map((answer) => (
              <Checkbox key={answer.id} value={answer.id} style={{ padding: '5px 10px' }}>
                {answer.content}
              </Checkbox>
            ))}
          </Checkbox.Group>
        )}

        {questionType?.code === CODIFICATION_QUESTION_TYPE.TEXT_INPUT && (
          <div>
            <Input
              placeholder={t('exercise.textPlaceholder')}
              onChange={(e) => handleAnswersText(e.target.value)}
              style={{ borderRadius: '8px', padding: '10px', width: '100%' }}
              value={answers?.[0]?.user_input}
            />
          </div>
        )}

        {questionType?.code === CODIFICATION_QUESTION_TYPE.NUMERIC_INPUT && (
          <div>
            <InputNumber
              placeholder={t('exercise.textPlaceholder')}
              onChange={(value) => handleAnswersText(String(value))}
              style={{ borderRadius: '8px', padding: '10px', width: '100%' }}
              value={Number(answers?.[0]?.user_input)}
            />
          </div>
        )}

        <Button
          type="primary"
          onClick={validateAnswer}
          style={{
            width: '100%',
            maxWidth: '300px',
            marginTop: 20,
            borderRadius: '8px',
            backgroundColor: '#1890ff',
            border: 'none',
          }}
          loading={onSave}
        >
          {t('button.submit')}
        </Button>
      </Card>
    </div>
  );
};

export default QuestionViewer;
